import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Alert, Modal, Button, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';

import selectIsOverlayerOpened from '../../core/selectors/overlayer';
import { doUpsertWaitingRoom, setCreateWaitingRoomFormField, resetCreateWaitingRoomForm } from '../actions/waiting-rooms';
import { closeOverlayer } from '../../core/actions/overlayer';
import { validateDate, validateOutflow, validateUrl } from '../utils/validators';
import { selectCreateWaitingRoomFormData } from '../selectors/waiting-rooms';
import { CREATE_WAITING_ROOM_OVERLAYER } from '../constants/overlayers';

const AddWaitingRoom = ({ selectedWaitingRoom, setSelectedWaitingRoom }) => {
    const dispatch = useDispatch();

    const form = useSelector((state) => selectCreateWaitingRoomFormData(state));
    const { name, title, startDate, endDate, outflow, defaultReturnUrl, entranceValidityInMinutes } = form;

    const isOpen = useSelector((state) => selectIsOverlayerOpened(state, CREATE_WAITING_ROOM_OVERLAYER));
    const handleClose = () => {
        setSelectedWaitingRoom(null);
        dispatch(closeOverlayer(CREATE_WAITING_ROOM_OVERLAYER));
    };

    useEffect(() => {
        if (selectedWaitingRoom) {
            dispatch(setCreateWaitingRoomFormField('name', selectedWaitingRoom.name, true));
            dispatch(setCreateWaitingRoomFormField('title', selectedWaitingRoom.title, true));
            dispatch(setCreateWaitingRoomFormField('startDate', selectedWaitingRoom.startDate, true));
            dispatch(setCreateWaitingRoomFormField('endDate', selectedWaitingRoom.endDate, true));
            dispatch(setCreateWaitingRoomFormField('outflow', selectedWaitingRoom.outflow, true));
            dispatch(setCreateWaitingRoomFormField('defaultReturnUrl', selectedWaitingRoom.defaultReturnUrl, true));
            dispatch(
                setCreateWaitingRoomFormField(
                    'entranceValidityInMinutes', selectedWaitingRoom.entranceValidityInMinutes, true,
                ),
            );
        } else {
            dispatch(resetCreateWaitingRoomForm());
        }
    }, [selectedWaitingRoom, isOpen]);

    const onCreate = () => {
        if (!name.isValid || !title.isValid || !outflow.isValid || !defaultReturnUrl.isValid) {
            return;
        }

        const payload = {
            name: name.value,
            title: title.value,
            startDate: startDate.value,
            endDate: endDate.value,
            outflow: outflow.value,
            defaultReturnUrl: defaultReturnUrl.value,
            entranceValidityInMinutes: entranceValidityInMinutes.value,
        };

        if (selectedWaitingRoom) {
            payload.id = selectedWaitingRoom.id;
        }

        dispatch(doUpsertWaitingRoom(payload));
        dispatch(resetCreateWaitingRoomForm());
        handleClose();
    };

    return (
        <div>
            <Modal show={isOpen} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Waiting Room Settings</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor='Name'>Name</Form.Label>
                        <Form.Control
                            value={name.value}
                            onChange={(e) => {
                                const { value } = e.target;
                                dispatch(setCreateWaitingRoomFormField('name', value, true));
                            }}
                            aria-label="Name"
                            aria-describedby="basic-addon1"
                            isInvalid={!name.isValid}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label htmlFor='Title'>Title</Form.Label>
                        <Form.Control
                            value={title.value}
                            onChange={(e) => {
                                const { value } = e.target;
                                dispatch(setCreateWaitingRoomFormField('title', value, true));
                            }}
                            aria-label="Title"
                            aria-describedby="basic-addon1"
                            isInvalid={!title.isValid}
                        />
                    </Form.Group>

                    <Row>
                        <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className='d-block' htmlFor='start-date'>Start Date</Form.Label>
                                <DatePicker
                                    selected={startDate.value ? new Date(startDate.value) : null}
                                    onChange={(value) => {
                                        const isValid = validateDate(value);
                                        dispatch(setCreateWaitingRoomFormField('startDate', value, isValid));
                                    }}
                                    showTimeSelect
                                    dateFormat="Pp"
                                    className="form-control"
                                    aria-label="Start Date"
                                    timeIntervals={15}
                                />
                                {!startDate.isValid && <Alert className='m-2' variant="danger">Date is not valid!</Alert>}
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className='d-block' htmlFor='End Date'>End Date</Form.Label>
                                <DatePicker
                                    selected={endDate.value ? new Date(endDate.value) : null}
                                    onChange={(value) => {
                                        const isValid = validateDate(value);
                                        dispatch(setCreateWaitingRoomFormField('endDate', value, isValid));
                                    }}
                                    showTimeSelect
                                    dateFormat="Pp"
                                    className="form-control"
                                    aria-label="End Date"
                                    timeIntervals={15}
                                />
                                {!endDate.isValid && <Alert className='m-2' variant="danger">Date is not valid!</Alert>}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label htmlFor='outflow'>Outflow</Form.Label>
                        <Form.Control
                            type="number"
                            value={outflow.value}
                            onChange={(e) => {
                                const { value } = e.target;
                                const isValid = validateOutflow(value);
                                dispatch(setCreateWaitingRoomFormField('outflow', value, isValid));
                            }}
                            aria-label="Outflow"
                            isInvalid={!outflow.isValid}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label htmlFor='default-return-url'>Default Return URL</Form.Label>
                        <Form.Control
                            type="url"
                            value={defaultReturnUrl.value}
                            onChange={(e) => {
                                const { value } = e.target;
                                const isValid = validateUrl(value);
                                dispatch(setCreateWaitingRoomFormField('defaultReturnUrl', value, isValid));
                            }}
                            aria-label="Default Return URL"
                            isInvalid={!defaultReturnUrl.isValid}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label htmlFor='entrance-validity'>Entrance Validity (minutes)</Form.Label>
                        <Form.Control
                            type="number"
                            value={entranceValidityInMinutes.value}
                            onChange={(e) => {
                                const { value } = e.target;
                                dispatch(setCreateWaitingRoomFormField('entranceValidityInMinutes', value, true));
                            }}
                            aria-label="Entrance validity (minutes)"
                        />
                    </Form.Group>

                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={onCreate}>
                        {selectedWaitingRoom ? 'Save' : 'Add'}
                    </Button>
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AddWaitingRoom;
